import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "../../services/adminApi";

const EMPTY = "-";

function asId(value) {
  if (!value) return "";
  if (typeof value === "object") return String(value._id || value.id || "");
  return String(value);
}

function compact(parts) {
  return parts.map((part) => String(part || "").trim()).filter(Boolean).join(" ");
}

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString();
}

function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
}

function dateSort(value) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.getTime();
}

function formatAddress(address = {}) {
  const line1 = address.street || "";
  const cityStateZip = [
    compact([address.city, address.state]),
    address.zip,
  ].filter(Boolean).join(" ");
  return [line1, cityStateZip].filter(Boolean).join(", ");
}

function normalizeStatus(value) {
  return String(value || "").replace(/_/g, " ");
}

function makeReportRow(driver, vehicle) {
  const driverId = asId(driver?._id);
  const vehicleId = asId(vehicle?._id);
  const driverName = driver ? compact([driver.firstName, driver.lastName]) : "";
  const vehicleName = vehicle ? compact([vehicle.year, vehicle.make, vehicle.model]) : "";

  return {
    key: `${driverId || "no-driver"}:${vehicleId || "no-vehicle"}`,
    driverId,
    vehicleId,
    assignment: driver && vehicle ? "Assigned" : driver ? "Driver without vehicle" : "Vehicle without driver",

    driverName,
    driverEmail: driver?.email || "",
    driverPhone: driver?.phone || "",
    driverStatus: driver?.status || "",
    enrollmentStatus: normalizeStatus(driver?.enrollmentStatus || "not_started"),
    enrollmentCompletedAt: driver?.enrollmentCompletedAt || "",
    profileCompleted: driver ? (driver.profileCompleted ? "Yes" : "No") : "",
    dateOfBirth: driver?.dateOfBirth || "",
    address: formatAddress(driver?.address),
    licenseNumber: driver?.licenseNumber || "",
    licenseState: driver?.licenseState || "",
    licenseClass: driver?.licenseClass || "",
    licenseExpiry: driver?.licenseExpiry || "",
    dotMedicalNumber: driver?.dotMedicalNumber || "",
    dotMedicalExpiry: driver?.dotMedicalExpiry || "",
    llcName: driver?.llcName || "",
    ein: driver?.ein || "",
    cbiCheckDate: driver?.cbiCheckDate || "",
    cbiExpiryDate: driver?.cbiExpiryDate || "",
    emergencyContactName: driver?.emergencyContactName || "",
    emergencyContactPhone: driver?.emergencyContactPhone || "",
    emergencyContactRelationship: driver?.emergencyContactRelationship || "",
    driverNotes: driver?.notes || "",
    driverDropboxFolder: driver?.dropboxFolder || "",
    mainDriverId: asId(driver?.mainDriverId),
    driverCreatedBy: asId(driver?.createdBy),
    driverCreatedAt: driver?.createdAt || "",
    driverUpdatedAt: driver?.updatedAt || "",

    vehicleName,
    cabNumber: vehicle?.cabNumber || "",
    vehicleYear: vehicle?.year || "",
    vehicleMake: vehicle?.make || "",
    vehicleModel: vehicle?.model || "",
    licensePlate: vehicle?.licensePlate || "",
    vin: vehicle?.vin || "",
    color: vehicle?.color || "",
    vehicleStatus: vehicle?.status || "",
    vehicleNotes: vehicle?.notes || "",
    vehicleCreatedAt: vehicle?.createdAt || "",
    vehicleUpdatedAt: vehicle?.updatedAt || "",
  };
}

function buildReportRows(drivers, vehicles) {
  const driverMap = new Map(drivers.map((driver) => [asId(driver._id), driver]));
  const driverVehicleCounts = new Map();
  const rows = vehicles.map((vehicle) => {
    const driverId = asId(vehicle.assignedDriverId);
    if (driverId) driverVehicleCounts.set(driverId, (driverVehicleCounts.get(driverId) || 0) + 1);
    return makeReportRow(driverMap.get(driverId), vehicle);
  });

  drivers.forEach((driver) => {
    const driverId = asId(driver._id);
    if (!driverVehicleCounts.has(driverId)) rows.push(makeReportRow(driver, null));
  });

  return rows;
}

const COLUMNS = [
  { key: "driverName", label: "Driver", get: (row) => row.driverName, href: (row) => row.driverId && `/enrollme/admin/drivers/${row.driverId}` },
  { key: "vehicleName", label: "Vehicle", get: (row) => row.vehicleName, href: (row) => row.vehicleId && `/enrollme/admin/vehicles/${row.vehicleId}` },
  { key: "cabNumber", label: "Cab #", get: (row) => row.cabNumber },
  { key: "assignment", label: "Assignment", get: (row) => row.assignment },
  { key: "driverStatus", label: "Driver Status", get: (row) => row.driverStatus },
  { key: "enrollmentStatus", label: "Enrollment", get: (row) => row.enrollmentStatus },
  { key: "driverPhone", label: "Phone", get: (row) => row.driverPhone },
  { key: "driverEmail", label: "Email", get: (row) => row.driverEmail },
  { key: "dateOfBirth", label: "Date of Birth", get: (row) => formatDate(row.dateOfBirth), sortValue: (row) => dateSort(row.dateOfBirth) },
  { key: "address", label: "Address", get: (row) => row.address },
  { key: "licenseNumber", label: "License #", get: (row) => row.licenseNumber },
  { key: "licenseState", label: "License State", get: (row) => row.licenseState },
  { key: "licenseClass", label: "License Class", get: (row) => row.licenseClass },
  { key: "licenseExpiry", label: "License Expiry", get: (row) => formatDate(row.licenseExpiry), sortValue: (row) => dateSort(row.licenseExpiry) },
  { key: "dotMedicalNumber", label: "DOT Medical #", get: (row) => row.dotMedicalNumber },
  { key: "dotMedicalExpiry", label: "DOT Medical Expiry", get: (row) => formatDate(row.dotMedicalExpiry), sortValue: (row) => dateSort(row.dotMedicalExpiry) },
  { key: "llcName", label: "Business / LLC", get: (row) => row.llcName },
  { key: "ein", label: "EIN", get: (row) => row.ein },
  { key: "cbiCheckDate", label: "CBI Check Date", get: (row) => formatDate(row.cbiCheckDate), sortValue: (row) => dateSort(row.cbiCheckDate) },
  { key: "cbiExpiryDate", label: "CBI Expiry", get: (row) => formatDate(row.cbiExpiryDate), sortValue: (row) => dateSort(row.cbiExpiryDate) },
  { key: "emergencyContactName", label: "Emergency Contact", get: (row) => row.emergencyContactName },
  { key: "emergencyContactPhone", label: "Emergency Phone", get: (row) => row.emergencyContactPhone },
  { key: "emergencyContactRelationship", label: "Emergency Relationship", get: (row) => row.emergencyContactRelationship },
  { key: "profileCompleted", label: "Profile Complete", get: (row) => row.profileCompleted },
  { key: "vehicleYear", label: "Year", get: (row) => row.vehicleYear, sortValue: (row) => row.vehicleYear },
  { key: "vehicleMake", label: "Make", get: (row) => row.vehicleMake },
  { key: "vehicleModel", label: "Model", get: (row) => row.vehicleModel },
  { key: "licensePlate", label: "Plate", get: (row) => row.licensePlate },
  { key: "vin", label: "VIN", get: (row) => row.vin },
  { key: "color", label: "Color", get: (row) => row.color },
  { key: "vehicleStatus", label: "Vehicle Status", get: (row) => row.vehicleStatus },
  { key: "driverNotes", label: "Driver Notes", get: (row) => row.driverNotes },
  { key: "vehicleNotes", label: "Vehicle Notes", get: (row) => row.vehicleNotes },
  { key: "driverCreatedAt", label: "Driver Created", get: (row) => formatDateTime(row.driverCreatedAt), sortValue: (row) => dateSort(row.driverCreatedAt) },
  { key: "driverUpdatedAt", label: "Driver Updated", get: (row) => formatDateTime(row.driverUpdatedAt), sortValue: (row) => dateSort(row.driverUpdatedAt) },
  { key: "enrollmentCompletedAt", label: "Enrollment Completed", get: (row) => formatDateTime(row.enrollmentCompletedAt), sortValue: (row) => dateSort(row.enrollmentCompletedAt) },
  { key: "vehicleCreatedAt", label: "Vehicle Created", get: (row) => formatDateTime(row.vehicleCreatedAt), sortValue: (row) => dateSort(row.vehicleCreatedAt) },
  { key: "vehicleUpdatedAt", label: "Vehicle Updated", get: (row) => formatDateTime(row.vehicleUpdatedAt), sortValue: (row) => dateSort(row.vehicleUpdatedAt) },
  { key: "driverId", label: "Driver ID", get: (row) => row.driverId },
  { key: "vehicleId", label: "Vehicle ID", get: (row) => row.vehicleId },
  { key: "mainDriverId", label: "Main Driver ID", get: (row) => row.mainDriverId },
  { key: "driverCreatedBy", label: "Created By", get: (row) => row.driverCreatedBy },
  { key: "driverDropboxFolder", label: "Dropbox Folder", get: (row) => row.driverDropboxFolder },
];

function getColumnValue(column, row) {
  return column.get(row) || "";
}

function compareRows(a, b, column, direction) {
  const getValue = column.sortValue || column.get;
  const left = getValue(a);
  const right = getValue(b);
  const leftEmpty = left === undefined || left === null || left === "";
  const rightEmpty = right === undefined || right === null || right === "";
  if (leftEmpty && rightEmpty) return 0;
  if (leftEmpty) return 1;
  if (rightEmpty) return -1;

  let result;
  if (typeof left === "number" && typeof right === "number") {
    result = left - right;
  } else {
    result = String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: "base" });
  }
  return direction === "asc" ? result : -result;
}

function csvEscape(value) {
  const text = String(value || "");
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function htmlEscape(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function downloadFile(filename, mimeType, content) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function exportRows(rows, type) {
  const date = new Date().toISOString().slice(0, 10);
  const filename = `enrollme-driver-vehicle-report-${date}.${type === "csv" ? "csv" : "xls"}`;
  const tableRows = [
    COLUMNS.map((column) => column.label),
    ...rows.map((row) => COLUMNS.map((column) => getColumnValue(column, row))),
  ];

  if (type === "csv") {
    const content = tableRows.map((row) => row.map(csvEscape).join(",")).join("\r\n");
    downloadFile(filename, "text/csv;charset=utf-8", content);
    return;
  }

  const html = `<!doctype html>
<html>
  <head><meta charset="utf-8" /></head>
  <body>
    <table>
      <thead><tr>${COLUMNS.map((column) => `<th>${htmlEscape(column.label)}</th>`).join("")}</tr></thead>
      <tbody>
        ${rows.map((row) => `<tr>${COLUMNS.map((column) => `<td>${htmlEscape(getColumnValue(column, row))}</td>`).join("")}</tr>`).join("")}
      </tbody>
    </table>
  </body>
</html>`;
  downloadFile(filename, "application/vnd.ms-excel;charset=utf-8", html);
}

function SortIcon({ active, direction }) {
  if (!active) {
    return (
      <svg className="w-3.5 h-3.5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
      </svg>
    );
  }
  return (
    <svg className="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={direction === "asc" ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
    </svg>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500 mt-0.5">{label}</p>
    </div>
  );
}

export default function DriverVehicleReport() {
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sort, setSort] = useState({ key: "driverName", direction: "asc" });

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    adminApi.getDriverVehicleReport()
      .then(({ drivers: driverList = [], vehicles: vehicleList = [] }) => {
        if (!alive) return;
        setDrivers(driverList);
        setVehicles(vehicleList);
      })
      .catch((err) => {
        if (alive) setError(err.message || "Could not load report.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => { alive = false; };
  }, []);

  const rows = useMemo(() => buildReportRows(drivers, vehicles), [drivers, vehicles]);

  const visibleRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = term
      ? rows.filter((row) => COLUMNS.some((column) => String(getColumnValue(column, row)).toLowerCase().includes(term)))
      : rows;
    const column = COLUMNS.find((item) => item.key === sort.key) || COLUMNS[0];
    return [...filtered].sort((a, b) => compareRows(a, b, column, sort.direction));
  }, [rows, search, sort]);

  const stats = useMemo(() => {
    const assignedVehicleIds = vehicles.filter((vehicle) => asId(vehicle.assignedDriverId)).map((vehicle) => asId(vehicle._id));
    const driverIdsWithVehicles = new Set(vehicles.map((vehicle) => asId(vehicle.assignedDriverId)).filter(Boolean));
    return {
      totalRows: rows.length,
      assignedVehicles: assignedVehicleIds.length,
      unassignedVehicles: vehicles.length - assignedVehicleIds.length,
      driversWithoutVehicles: drivers.filter((driver) => !driverIdsWithVehicles.has(asId(driver._id))).length,
    };
  }, [drivers, rows.length, vehicles]);

  function handleSort(column) {
    setSort((current) => ({
      key: column.key,
      direction: current.key === column.key && current.direction === "asc" ? "desc" : "asc",
    }));
  }

  return (
    <div>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Driver &amp; Vehicle Report</h1>
          <p className="text-sm text-gray-500 mt-1">Combined driver and assigned vehicle records in one searchable table.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => exportRows(visibleRows, "csv")}
            disabled={visibleRows.length === 0}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 16V4m0 12l-4-4m4 4l4-4M4 20h16" />
            </svg>
            CSV
          </button>
          <button
            type="button"
            onClick={() => exportRows(visibleRows, "excel")}
            disabled={visibleRows.length === 0}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-6m4 6V7m4 10v-4M5 21h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            Excel
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <Stat label="Report Rows" value={stats.totalRows} />
        <Stat label="Assigned Vehicles" value={stats.assignedVehicles} />
        <Stat label="Unassigned Vehicles" value={stats.unassignedVehicles} />
        <Stat label="Drivers Without Vehicle" value={stats.driversWithoutVehicles} />
      </div>

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-md">
          <svg className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35m1.85-5.15a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search driver, cab #, vehicle, phone, plate, VIN, notes..."
            className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <p className="text-sm text-gray-500">
          Showing <span className="font-semibold text-gray-900">{visibleRows.length}</span> of <span className="font-semibold text-gray-900">{rows.length}</span> rows
        </p>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400">Loading...</div>
        ) : visibleRows.length === 0 ? (
          <div className="p-8 text-center text-gray-400">No matching driver or vehicle records.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {COLUMNS.map((column) => (
                    <th key={column.key} className="whitespace-nowrap px-4 py-3 text-left font-medium text-gray-600">
                      <button
                        type="button"
                        onClick={() => handleSort(column)}
                        className="inline-flex items-center gap-1.5 rounded text-left hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        aria-label={`Sort by ${column.label}`}
                      >
                        <span>{column.label}</span>
                        <SortIcon active={sort.key === column.key} direction={sort.direction} />
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visibleRows.map((row) => (
                  <tr key={row.key} className="hover:bg-gray-50">
                    {COLUMNS.map((column) => {
                      const value = getColumnValue(column, row);
                      const href = column.href?.(row);
                      return (
                        <td key={column.key} className="max-w-xs whitespace-nowrap px-4 py-3 text-gray-600">
                          {href && value ? (
                            <Link to={href} className="font-medium text-blue-600 hover:underline">
                              {value}
                            </Link>
                          ) : (
                            <span className={value ? "" : "text-gray-400"}>{value || EMPTY}</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
